/**
 * Display status for a support ticket in the staff Inbox.
 *
 * The stored `status` says who OWNS the ticket, not whose turn it is to talk.
 * Once an agent takes a ticket it stays `assigned` forever, so the queue showed
 * a flat "In progress" whether the agent had just answered or the user had
 * written back three times and was waiting. This splits `assigned` in two using
 * the role of the LAST message — the same signal the backend already uses to
 * raise a `user_reply` alert (support.service#getMyAlerts):
 *
 *   - the agent spoke last  -> the ball is with the user  ("Waiting on client")
 *   - anyone else spoke last -> the ball is with us        ("Needs reply")
 *
 * The "anyone else" branch is deliberate: a ticket taken over right after the
 * AI handoff has an `ai` message last and still owes the user a human answer.
 *
 * The queue endpoint projects `lastMessageRole`; the ticket detail ships the
 * whole `messages` array. Both shapes are accepted.
 */
export type SupportDisplayStatus =
  | "with_ai"
  | "awaiting_human"
  | "needs_reply"
  | "waiting_on_client"
  | "closed";

type TicketLike = {
  status?: string | null;
  lastMessageRole?: string | null;
  messages?: Array<{ role?: string | null }> | null;
};

export const SUPPORT_STATUS_LABEL: Record<SupportDisplayStatus, string> = {
  with_ai: "With AI",
  awaiting_human: "Waiting",
  needs_reply: "Needs reply",
  waiting_on_client: "Waiting on client",
  closed: "Closed",
};

export const SUPPORT_STATUS_COLOR: Record<SupportDisplayStatus, string> = {
  with_ai: "#7C6FD6",
  awaiting_human: "#E8A33D",
  needs_reply: "#E0574F",
  waiting_on_client: "#4F7A96",
  closed: "#9aa0a6",
};

export const isClosedSupportStatus = (status?: string | null): boolean =>
  status === "resolved" || status === "closed";

/** Role of the last message, from either payload shape. */
const lastRoleOf = (ticket?: TicketLike | null): string | null => {
  if (!ticket) return null;
  if (ticket.lastMessageRole) return String(ticket.lastMessageRole);
  const messages = Array.isArray(ticket.messages) ? ticket.messages : [];
  const last = messages[messages.length - 1];
  return last?.role ? String(last.role) : null;
};

export const supportDisplayStatus = (ticket?: TicketLike | null): SupportDisplayStatus => {
  const status = ticket?.status ?? "";
  if (isClosedSupportStatus(status)) return "closed";
  if (status === "with_ai") return "with_ai";
  if (status === "awaiting_human") return "awaiting_human";
  if (status === "assigned") {
    return lastRoleOf(ticket) === "staff" ? "waiting_on_client" : "needs_reply";
  }
  // Unknown/legacy status: treat it as an open ticket that owes an answer
  // rather than silently rendering a blank chip.
  return "needs_reply";
};

/** True when the ticket is open and the next move is the agent's. */
export const supportNeedsReply = (ticket?: TicketLike | null): boolean =>
  supportDisplayStatus(ticket) === "needs_reply";

/**
 * How long the user has been waiting, for the global alert cards.
 *
 * A ticket only counts as "stalled" after the backend reminder window, so a
 * card that just popped reports the event ("User replied") and does not scold
 * an agent who is mid-sentence. Mirror of
 * `backend/src/modules/support/reply-reminder.util.ts` — change both together.
 */
export const STALLED_AFTER_MS = 15 * 60 * 1000;

const waitedMs = (since: unknown, now: number): number => {
  const at = since ? new Date(since as never).getTime() : NaN;
  return Number.isFinite(at) ? now - at : NaN;
};

/** "3m" / "2h 5m" / "2d 4h". Empty string when there is no usable timestamp. */
export const waitingLabel = (since: unknown, now: number = Date.now()): string => {
  const ms = waitedMs(since, now);
  if (!Number.isFinite(ms)) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h${mins % 60 ? ` ${mins % 60}m` : ""}`;
  const days = Math.floor(hours / 24);
  return `${days}d${hours % 24 ? ` ${hours % 24}h` : ""}`;
};

export const isStalledWait = (since: unknown, now: number = Date.now()): boolean => {
  const ms = waitedMs(since, now);
  return Number.isFinite(ms) && ms >= STALLED_AFTER_MS;
};
